import { User } from "@/services/backend/auth/types/user";
import { Role, RoleList } from "@/shared/lib/authorization/roles";

export interface DevToken extends User {
  token: string;
}

export type DevTokenOverrides = Pick<
  DevToken,
  "roles" | "companies" | "branches"
>;

const devTokenStorageKey = "gsm.dev-token-overrides.v1";

function devTokenValuesFromEnv(): DevTokenOverrides {
  return {
    roles: import.meta.env.VITE_DEV_TOKEN_ROLES
      ? (import.meta.env.VITE_DEV_TOKEN_ROLES.split(",") as Role[])
      : [],
    companies: import.meta.env.VITE_DEV_TOKEN_COMPANIES
      ? import.meta.env.VITE_DEV_TOKEN_COMPANIES.split(",")
      : [],
    branches: import.meta.env.VITE_DEV_TOKEN_BRANCHES
      ? import.meta.env.VITE_DEV_TOKEN_BRANCHES.split(",")
      : [],
  };
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function readDevTokenOverrides(): DevTokenOverrides {
  const defaults = devTokenValuesFromEnv();
  if (import.meta.env.VITE_AUTH !== "dev" || typeof window === "undefined") {
    return defaults;
  }

  try {
    const raw = window.localStorage.getItem(devTokenStorageKey);
    if (!raw) return defaults;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !isStringArray((parsed as { roles?: unknown }).roles) ||
      !isStringArray((parsed as { companies?: unknown }).companies) ||
      !isStringArray((parsed as { branches?: unknown }).branches) ||
      !(parsed as { roles: string[] }).roles.every((role) =>
        RoleList.includes(role as Role),
      )
    ) {
      return defaults;
    }

    return parsed as DevTokenOverrides;
  } catch {
    return defaults;
  }
}

export function persistDevTokenOverrides(overrides: DevTokenOverrides) {
  if (import.meta.env.VITE_AUTH !== "dev" || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(devTokenStorageKey, JSON.stringify(overrides));
  } catch {
    return;
  }
}

export function clearDevTokenOverrides() {
  if (import.meta.env.VITE_AUTH !== "dev" || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(devTokenStorageKey);
  } catch {
    return;
  }
}

export function resetDevTokenToEnv(): DevTokenOverrides {
  const defaults = devTokenValuesFromEnv();
  devToken.roles = defaults.roles;
  devToken.companies = defaults.companies;
  devToken.branches = defaults.branches;
  return defaults;
}

const persistedDevTokenValues = readDevTokenOverrides();

export const devToken: DevToken = {
  token: import.meta.env.VITE_DEV_TOKEN,
  // a fixed ID makes it easier to test some cases,
  // e.g. process ownership after creating one and reloading
  // the page for some reason.
  id: "badb4f71-0804-42e8-b938-798be9cffe0f",
  roles: persistedDevTokenValues.roles,
  companies: persistedDevTokenValues.companies,
  branches: persistedDevTokenValues.branches,
  scopes: import.meta.env.VITE_DEV_TOKEN_SCOPES,
};
